import type { Flight, CreateFlightInput, UpdateFlightInput, FlightStats } from '@/types';
import { calculateDistance } from '@/utils';
import { apiFetch } from './apiClient';

/**
 * Flight service for managing flight data
 * Calls API Gateway endpoints backed by Lambda and DynamoDB
 */

/**
 * Get all flights for the current user
 */
export async function getFlights(): Promise<Flight[]> {
  const response = await apiFetch(
    `${import.meta.env.VITE_API_ENDPOINT}/flights`,
    {}
  );

  if (!response.ok) {
    throw new Error('Failed to fetch flights');
  }

  return response.json();
}

/**
 * Get a friend's flights (if they share them)
 */
export async function getFriendFlights(friendId: string): Promise<Flight[]> {
  const response = await apiFetch(
    `${import.meta.env.VITE_API_ENDPOINT}/friends/${friendId}/flights`,
    {}
  );

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error('This user does not share their flight details');
    }
    throw new Error('Failed to fetch friend flights');
  }

  return response.json();
}

/**
 * Create a new flight
 */
export async function createFlight(
  _userId: string,
  input: CreateFlightInput
): Promise<Flight> {
  const response = await apiFetch(
    `${import.meta.env.VITE_API_ENDPOINT}/flights`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to create flight');
  }

  return response.json();
}

/**
 * Delete a flight
 */
export async function deleteFlight(flightId: string): Promise<void> {
  const response = await apiFetch(
    `${import.meta.env.VITE_API_ENDPOINT}/flights/${flightId}`,
    {
      method: 'DELETE',
    }
  );

  if (!response.ok) {
    throw new Error('Failed to delete flight');
  }
}

/**
 * Update a flight
 */
export async function updateFlight(
  flightId: string,
  input: UpdateFlightInput
): Promise<Flight> {
  const response = await apiFetch(
    `${import.meta.env.VITE_API_ENDPOINT}/flights/${flightId}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to update flight');
  }

  return response.json();
}

/**
 * Calculate flight statistics
 */
export function calculateFlightStats(flights: Flight[]): FlightStats {
  const countries = new Set<string>();
  const airports = new Set<string>();
  let totalMiles = 0;
  let totalFlights = 0;

  flights.forEach((flight) => {
    // Count flights (return flights count as 2)
    totalFlights += flight.flightType === 'return' ? 2 : 1;

    // Track countries and airports
    countries.add(flight.fromAirport.country);
    countries.add(flight.toAirport.country);
    airports.add(flight.fromAirport.iata);
    airports.add(flight.toAirport.iata);

    // Calculate distance
    const distance = calculateDistance(
      flight.fromAirport.lat,
      flight.fromAirport.lon,
      flight.toAirport.lat,
      flight.toAirport.lon
    );

    // Double distance for return flights
    totalMiles += flight.flightType === 'return' ? distance * 2 : distance;
  });

  return {
    totalFlights,
    uniqueCountries: countries.size,
    totalMiles: Math.round(totalMiles),
    uniqueAirports: airports.size,
  };
}
